import { Pane } from "evergreen-ui";
import React from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./QuillImageStyles.css";
import ImageResize from 'quill-image-resize-module-react';

Quill.register('modules/imageResize', ImageResize);

// Register custom image format with width and height attributes
const ImageFormatAttributesList = [
  'alt',
  'height',
  'width',
  'style'
];

const BaseImageFormat = Quill.import('formats/image');
class ImageFormat extends BaseImageFormat {
  static formats(domNode) {
    return ImageFormatAttributesList.reduce((formats, attribute) => {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      return formats;
    }, {});
  }
  format(name, value) {
    if (ImageFormatAttributesList.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(ImageFormat, true);

// ---------------------------------------------------------------------------
// Utility: fetch an external image URL and return it as a base64 data URL.
// Uses a proxy-less approach via canvas for same-origin or CORS-enabled images,
// falls back to inserting the raw URL if conversion fails.
// ---------------------------------------------------------------------------
function imageUrlToBase64(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        // CORS tainted – just use the original URL
        resolve(url);
      }
    };
    img.onerror = () => resolve(url);
    img.src = url;
  });
}

// ---------------------------------------------------------------------------
// Utility: resize a base64 (or blob-URL) image to the given width / height
// using an off-screen canvas. Returns a Promise<string> (new data-URL).
// ---------------------------------------------------------------------------
function resizeBase64Image(src, targetW, targetH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetW, targetH);
      // Keep PNG for transparency, use JPEG for large photos
      const mime = src.startsWith('data:image/jpeg') ? 'image/jpeg' : 'image/png';
      const quality = mime === 'image/jpeg' ? 0.85 : undefined;
      resolve(canvas.toDataURL(mime, quality));
    };
    img.onerror = () => resolve(src); // fallback – return unchanged
    img.src = src;
  });
}

class KRichTextArea extends React.Component {
  constructor(props) {
    super(props);
    this.quillRef = React.createRef();
  }

  componentDidMount() {
    const quill = this.quillRef.current && this.quillRef.current.getEditor();
    if (!quill) return;

    // --- Drag & drop from web (external image URLs) -----------------------
    quill.root.addEventListener('drop', this.handleDrop, false);

    // --- Paste images from clipboard --------------------------------------
    quill.root.addEventListener('paste', this.handlePaste, false);

    // --- Resample images after resize drag ends ---------------------------
    // The resize module sets img.width on mousemove and fires mouseup when
    // the drag ends. We listen globally for mouseup and check if any image
    // inside the editor was just resized.
    document.addEventListener('mouseup', this.handleMouseUp, false);
  }

  componentWillUnmount() {
    const quill = this.quillRef.current && this.quillRef.current.getEditor();
    if (quill) {
      quill.root.removeEventListener('drop', this.handleDrop, false);
      quill.root.removeEventListener('paste', this.handlePaste, false);
    }
    document.removeEventListener('mouseup', this.handleMouseUp, false);
    clearTimeout(this._resampleTimer);
  }

  // After any mouseup, scan for images whose displayed width differs from
  // their intrinsic data size and resample them. Debounced briefly so the
  // resize module finishes updating first.
  handleMouseUp = () => {
    clearTimeout(this._resampleTimer);
    this._resampleTimer = setTimeout(() => this._resampleAllImages(), 300);
  };

  _resampleAllImages = async () => {
    const quill = this.quillRef.current && this.quillRef.current.getEditor();
    if (!quill) return;

    const imgs = quill.root.querySelectorAll('img');
    let changed = false;

    for (const img of imgs) {
      const src = img.getAttribute('src') || '';
      if (!src.startsWith('data:') && !/^https?:\/\//i.test(src)) continue;

      // The resize module sets img.width (attribute), but never height.
      // Read the actual rendered dimensions from the element.
      const displayW = img.width;
      const displayH = img.height;
      if (!displayW || !displayH) continue;

      // Probe the intrinsic size of the current src
      const intrinsic = await new Promise((res) => {
        const probe = new Image();
        probe.onload = () => res({ w: probe.naturalWidth, h: probe.naturalHeight });
        probe.onerror = () => res(null);
        probe.src = src;
      });
      if (!intrinsic) continue;

      // Only resample if displayed size is meaningfully smaller (>10% reduction)
      if (displayW >= intrinsic.w * 0.9) continue;

      // Resample: destructively replace the base64 data with the smaller version.
      // If you later resize the image larger, it WILL be blurry — that's intended
      // to keep the saved payload small.
      const resized = await resizeBase64Image(src, displayW, displayH);
      img.setAttribute('src', resized);
      changed = true;
    }

    if (changed) {
      // Sync Quill's internal delta with the DOM changes and fire onChange
      quill.update('user');
    }
  };

  // Insert an image (data-URL or regular URL) at the current cursor position
  insertImage = (src) => {
    const quill = this.quillRef.current && this.quillRef.current.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true) || { index: quill.getLength() };
    quill.insertEmbed(range.index, 'image', src);
    quill.setSelection(range.index + 1);
  };

  // Handle dropped images – files AND urls dragged from other browser tabs
  handleDrop = async (e) => {
    const dt = e.dataTransfer;
    if (!dt) return;

    // 1. Dropped files (local or dragged-from-browser image files)
    if (dt.files && dt.files.length > 0) {
      const file = dt.files[0];
      if (file.type.startsWith('image/')) {
        e.preventDefault();
        e.stopPropagation();
        const reader = new FileReader();
        reader.onload = (ev) => this.insertImage(ev.target.result);
        reader.readAsDataURL(file);
        return;
      }
    }

    // 2. Image URL dragged from a web page (text/uri-list or text/html)
    const html = dt.getData('text/html');
    const uri = dt.getData('text/uri-list') || dt.getData('text/plain');

    // Try to extract an <img src="..."> from the dropped HTML
    let imgSrc = '';
    if (html) {
      const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (m) imgSrc = m[1];
    }
    if (!imgSrc && uri && /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(uri)) {
      imgSrc = uri;
    }

    if (imgSrc) {
      e.preventDefault();
      e.stopPropagation();
      const base64 = await imageUrlToBase64(imgSrc);
      this.insertImage(base64);
    }
  };

  // Handle paste – support pasting images from clipboard
  handlePaste = async (e) => {
    const cb = e.clipboardData;
    if (!cb) return;

    // Check for image file in clipboard items
    for (const item of cb.items || []) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        e.stopPropagation();
        const file = item.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => this.insertImage(ev.target.result);
        reader.readAsDataURL(file);
        return;
      }
    }

    // Check for pasted image URL as plain text
    const text = (cb.getData('text/plain') || '').trim();
    if (text && /^https?:\/\/.+\.(png|jpe?g|gif|webp|svg|bmp)(\?.*)?$/i.test(text)) {
      e.preventDefault();
      e.stopPropagation();
      const base64 = await imageUrlToBase64(text);
      this.insertImage(base64);
    }
  };

  // Image toolbar button handler – shows a picker dialog (file or URL)
  imageHandler = () => {
    // Create a small modal for choosing between file upload and URL
    const overlay = document.createElement('div');
    overlay.className = 'ql-image-picker-overlay';

    const dialog = document.createElement('div');
    dialog.className = 'ql-image-picker-dialog';
    dialog.innerHTML = `
      <div class="ql-image-picker-title">Inserează imagine</div>
      <div class="ql-image-picker-tabs">
        <button class="ql-image-picker-tab active" data-tab="file">Fișier</button>
        <button class="ql-image-picker-tab" data-tab="url">URL</button>
      </div>
      <div class="ql-image-picker-body">
        <div class="ql-image-picker-file active" data-panel="file">
          <div class="ql-image-picker-dropzone" id="ql-dropzone">
            <p>Trage o imagine aici sau</p>
            <button class="ql-image-picker-browse">Alege fișier</button>
            <input type="file" accept="image/*" style="display:none" />
          </div>
          <div class="ql-image-picker-preview" style="display:none">
            <img />
            <button class="ql-image-picker-remove">✕</button>
          </div>
        </div>
        <div class="ql-image-picker-url" data-panel="url" style="display:none">
          <input type="text" placeholder="https://exemplu.com/imagine.jpg" class="ql-image-picker-url-input" />
          <div class="ql-image-picker-url-preview" style="display:none">
            <img />
          </div>
        </div>
      </div>
      <div class="ql-image-picker-actions">
        <button class="ql-image-picker-cancel">Anulează</button>
        <button class="ql-image-picker-insert" disabled>Inserează</button>
      </div>
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // State
    let selectedSrc = null;

    const close = () => {
      document.body.removeChild(overlay);
    };

    // Tab switching
    const tabs = dialog.querySelectorAll('.ql-image-picker-tab');
    const panelFile = dialog.querySelector('[data-panel="file"]');
    const panelUrl = dialog.querySelector('[data-panel="url"]');
    const insertBtn = dialog.querySelector('.ql-image-picker-insert');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');
        if (tab.dataset.tab === 'file') {
          panelFile.style.display = '';
          panelUrl.style.display = 'none';
        } else {
          panelFile.style.display = 'none';
          panelUrl.style.display = '';
        }
      });
    });

    // File tab
    const dropzone = dialog.querySelector('#ql-dropzone');
    const fileInput = dialog.querySelector('input[type="file"]');
    const browseBtn = dialog.querySelector('.ql-image-picker-browse');
    const previewWrap = dialog.querySelector('.ql-image-picker-preview');
    const previewImg = previewWrap.querySelector('img');
    const removeBtn = dialog.querySelector('.ql-image-picker-remove');

    const loadFile = (file) => {
      if (!file || !file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        selectedSrc = ev.target.result;
        previewImg.src = selectedSrc;
        previewWrap.style.display = '';
        dropzone.style.display = 'none';
        insertBtn.disabled = false;
      };
      reader.readAsDataURL(file);
    };

    browseBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', () => {
      if (fileInput.files[0]) loadFile(fileInput.files[0]);
    });

    dropzone.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      dropzone.classList.add('dragover');
    });
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
    dropzone.addEventListener('drop', (ev) => {
      ev.preventDefault();
      dropzone.classList.remove('dragover');
      if (ev.dataTransfer.files[0]) loadFile(ev.dataTransfer.files[0]);
    });

    removeBtn.addEventListener('click', () => {
      selectedSrc = null;
      previewWrap.style.display = 'none';
      dropzone.style.display = '';
      insertBtn.disabled = true;
      fileInput.value = '';
    });

    // URL tab
    const urlInput = dialog.querySelector('.ql-image-picker-url-input');
    const urlPreviewWrap = dialog.querySelector('.ql-image-picker-url-preview');
    const urlPreviewImg = urlPreviewWrap.querySelector('img');
    let urlDebounce;

    urlInput.addEventListener('input', () => {
      clearTimeout(urlDebounce);
      const val = urlInput.value.trim();
      urlDebounce = setTimeout(() => {
        if (/^https?:\/\/.+/i.test(val)) {
          urlPreviewImg.src = val;
          urlPreviewImg.onload = () => {
            urlPreviewWrap.style.display = '';
            selectedSrc = val;
            insertBtn.disabled = false;
          };
          urlPreviewImg.onerror = () => {
            urlPreviewWrap.style.display = 'none';
            selectedSrc = null;
            insertBtn.disabled = true;
          };
        } else {
          urlPreviewWrap.style.display = 'none';
          selectedSrc = null;
          insertBtn.disabled = true;
        }
      }, 400);
    });

    // Actions
    overlay.addEventListener('click', (ev) => {
      if (ev.target === overlay) close();
    });
    dialog.querySelector('.ql-image-picker-cancel').addEventListener('click', close);
    insertBtn.addEventListener('click', async () => {
      if (!selectedSrc) return;
      // For URL sources, attempt to convert to base64 for consistency
      let src = selectedSrc;
      if (src.startsWith('http')) {
        src = await imageUrlToBase64(src);
      }
      this.insertImage(src);
      close();
    });
  };

  render() {
    const modules = {
      clipboard: { matchVisual: false },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize', 'Toolbar'],
        handleStyles: {
          backgroundColor: 'black',
          border: 'none',
          color: 'white'
        },
        displayStyles: {
          backgroundColor: 'black',
          border: 'none',
          color: 'white'
        },
        toolbarStyles: {
          backgroundColor: 'black',
          border: 'none',
          color: 'white'
        },
        toolbarButtonStyles: {
          display: 'inline-block',
          width: '24px',
          height: '24px'
        },
        toolbarButtonSvgStyles: {
          fill: 'white',
          stroke: 'white',
          strokeWidth: '2'
        }
      },
      toolbar: {
        container: [
          ["bold", "italic", "underline", "strike"],
          ["blockquote", "code-block"],
          ["link", "image"],
          [{ header: 1 }, { header: 2 }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ script: "sub" }, { script: "super" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ direction: "rtl" }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          [{ align: [] }],
          ["clean"],
        ],
        handlers: {
          image: this.imageHandler,
        },
      },
    };

    const formats = [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "list",
      "bullet",
      "indent",
      "link",
      "image",
      "color",
      "background",
      "font",
      "align",
      "code-block",
      "script",
      "direction",
      "width",
      "height",
      "style"
    ];

    return (
      <Pane>
        <ReactQuill
          ref={this.quillRef}
          theme="snow"
          value={this.props.value}
          onChange={this.props.onChange}
          modules={modules}
          formats={formats}
        />
      </Pane>
    );
  }
}

export default KRichTextArea;
